import { useState } from 'react';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const SENSORS = ['gps', 'temp', 'humidity', 'bag', 'weight'];
const mono = { fontFamily: "'IBM Plex Mono', monospace" };

export const DeviceTypeManager = ({ types, onChanged, onClose }) => {
  const [form, setForm] = useState({ code: '', display_name: '', id_prefix: '', sensors: ['temp'], extra: '' });
  const [busy, setBusy] = useState(false);

  const toggleSensor = (s) => setForm(f => ({
    ...f, sensors: f.sensors.includes(s) ? f.sensors.filter(x => x !== s) : [...f.sensors, s],
  }));

  const create = async () => {
    if (!form.code || !form.display_name || !form.id_prefix) { toast.error('Code, display name and ID prefix are required'); return; }
    setBusy(true);
    try {
      await adminPost('/device-types', {
        code: form.code, display_name: form.display_name, id_prefix: form.id_prefix,
        sensors: form.sensors, extra_qc_items: form.extra.split('\n').filter(Boolean),
      });
      toast.success(`Device type ${form.code.toUpperCase()} created`);
      setForm({ code: '', display_name: '', id_prefix: '', sensors: ['temp'], extra: '' });
      const fresh = await adminGet('/device-types');
      onChanged(fresh);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="device-type-manager">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ color: '#1B1D1C' }}>Device types</h2>
          <button onClick={onClose} data-testid="type-manager-close" className="p-1.5 rounded hover:bg-[#FAF9F6]"><X className="w-5 h-5" /></button>
        </div>

        <table className="w-full text-sm mb-6">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-2 font-medium">Code</th><th className="font-medium">Display</th>
            <th className="font-medium">ID prefix</th><th className="font-medium">Sensors</th><th className="font-medium">QC items</th></tr></thead>
          <tbody>
            {types.map(t => (
              <tr key={t.code} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`type-row-${t.code}`}>
                <td className="py-2" style={mono}>{t.code}</td>
                <td>{t.display_name}</td>
                <td style={mono}>{t.id_prefix}</td>
                <td style={{ color: '#6b7069' }}>{(t.sensors || []).join(', ') || '—'}</td>
                <td style={mono}>{(t.qc_items || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="rounded-lg p-4" style={{ background: '#FAF9F6', border: '1px solid #EDEBE4' }}>
          <p className="text-sm font-bold mb-3" style={{ color: '#0B6B55' }}>ADD NEW TYPE</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Code</label>
              <Input data-testid="new-type-code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="MINI" className="bg-white border-[#D9D6CD]" style={mono} />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Display name</label>
              <Input data-testid="new-type-display" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
                placeholder="Mini" className="bg-white border-[#D9D6CD]" />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>ID prefix</label>
              <Input data-testid="new-type-prefix" value={form.id_prefix} onChange={e => setForm({ ...form, id_prefix: e.target.value.toUpperCase() })}
                placeholder="MIN" maxLength={10} className="bg-white border-[#D9D6CD]" style={mono} />
            </div>
          </div>
          <label className="text-xs block mb-1.5" style={{ color: '#4b504a' }}>Sensors — QC checklist adjusts automatically</label>
          <div className="flex gap-2 mb-3 flex-wrap">
            {SENSORS.map(s => (
              <button key={s} data-testid={`new-type-sensor-${s}`} onClick={() => toggleSensor(s)}
                className="px-3 py-1.5 rounded-full text-xs border transition-colors"
                style={form.sensors.includes(s)
                  ? { background: '#E7F2EE', borderColor: '#0B6B55', color: '#0B6B55' }
                  : { background: 'white', borderColor: '#D9D6CD', color: '#6b7069' }}>
                {s}
              </button>
            ))}
          </div>
          <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Extra QC checklist items (one per line, optional)</label>
          <textarea data-testid="new-type-extra-qc" value={form.extra} onChange={e => setForm({ ...form, extra: e.target.value })}
            rows={2} placeholder="e.g. Enclosure sealed and gasket checked"
            className="w-full rounded-md border p-2 text-sm bg-white mb-3" style={{ borderColor: '#D9D6CD' }} />
          <Button disabled={busy} data-testid="new-type-create-btn" onClick={create}
            className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>Create device type</Button>
        </div>
      </div>
    </div>
  );
};
